'use client';

import { useEffect } from 'react';
import { grantClarityConsent, injectClarity } from '@/lib/analytics/clarity';
import { flushQueuedIfGranted } from '@/lib/analytics/gtm';

// Simplified consent: always grant analytics_storage

export default function ConsentManager() {
  useEffect(() => {
    const w = window as any;
    // gtag/dataLayer shims
    w.dataLayer = w.dataLayer || [];
    w.gtag =
      w.gtag ||
      function () {
        // biome-ignore lint/complexity/noArguments: official gtag snippet pushes the arguments object as-is
        w.dataLayer.push(arguments);
      };
    // one-time default guard per page load
    if (typeof w.__consentDefaultSet === 'undefined') w.__consentDefaultSet = false;
    // simplified: no dedupe needed

    // Load GTM only after consent is granted (Basic Mode)
    function injectGTM(): Promise<void> {
      return new Promise((resolve) => {
        const GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_ID as string | undefined;
        const allowInDev = process.env.NEXT_PUBLIC_ENABLE_GTM_DEV === 'true';
        const isAllowedEnv = process.env.NODE_ENV === 'production' || allowInDev;
        if ((w as any).__gtmLoaded || !GTM_ID || !isAllowedEnv) {
          resolve();
          return;
        }
        (w as any).__gtmLoaded = true;
        // Bootstrap event per official snippet
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
        const s = document.createElement('script');
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
        s.onload = () => resolve();
        s.onerror = () => resolve();
        (document.head || document.documentElement).appendChild(s);
      });
    }

    async function grantAndInject() {
      // Consent default (once per page): always grant analytics
      if (!w.__consentDefaultSet) {
        w.gtag('consent', 'default', {
          ad_storage: 'denied',
          analytics_storage: 'granted',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          functionality_storage: 'granted',
          security_storage: 'granted',
          personalization_storage: 'denied',
        });
        w.__consentDefaultSet = true;
      }
      w.__analyticsGranted = true;
      await injectGTM();
      await injectClarity();
      grantClarityConsent({ analytics_Storage: 'granted', ad_Storage: 'denied' });
      setTimeout(() => {
        try {
          flushQueuedIfGranted?.();
          window.dispatchEvent(new CustomEvent('bs:analytics-granted'));
          window.dispatchEvent(new Event('resize'));
          window.dispatchEvent(new Event('scroll'));
        } catch {}
      }, 0);
    }

    // Schedule GTM after LCP/idle or user interaction to reduce unused JS on first paint
    const schedule = () => {
      // If user has Save-Data, skip loading GTM
      // @ts-expect-error
      if (navigator?.connection?.saveData) return;
      let triggered = false;
      const fire = () => {
        if (triggered) return;
        triggered = true;
        grantAndInject();
        removeListeners();
      };
      const removeListeners = () => {
        window.removeEventListener('pointerdown', fire);
        window.removeEventListener('keydown', fire);
      };
      window.addEventListener('pointerdown', fire, { once: true });
      window.addEventListener('keydown', fire, { once: true });
      try {
        const po = new PerformanceObserver((list) => {
          if (list.getEntries().length > 0) {
            po.disconnect();
            fire();
          }
        });
        // @ts-expect-error
        po.observe?.({ type: 'largest-contentful-paint', buffered: true });
      } catch {}
      // Fallback idle/timeout
      // @ts-expect-error
      const ric = window.requestIdleCallback || ((cb: any) => setTimeout(cb, 2000));
      ric(() => fire());
    };
    schedule();

    // no sync needed
  }, []);

  return null;
}
